import type { ConfigEconomicsItem, ComputedEconomics, ShippingPaidBy, EconomicsSettings } from "./types"
import type { BundleTypePerformance } from "@/lib/shopify/bundles/types"
import { zonedTimeToUtc } from "@/lib/date-utils"

// ---------------------------------------------------------------------------
// Lifetime-vs-period intersection (D4) — drives the dimmed-row UX
// ---------------------------------------------------------------------------

/**
 * Convert a YYYY-MM-DD calendar literal (Postgres `date` column for starts_at
 * / ends_at) into a UTC ms by interpreting it as midnight in the user's IANA
 * timezone. NaN on parse error so callers can fall back to "intersects =
 * true" (don't dim on bad data).
 */
function calendarLiteralToUtcMs(ymd: string, timezone: string): number {
  try {
    return zonedTimeToUtc(`${ymd}T00:00:00`, timezone).getTime()
  } catch {
    return Number.NaN
  }
}

/**
 * D4 — does the bundle's lifetime intersect the selected UI period?
 *
 *   starts_at >= period_end → bundle launched AT OR AFTER period ended → outside
 *   ends_at < period_start → bundle retired BEFORE period started → outside
 *   otherwise → intersects (normal row)
 *
 * `period_end` is exclusive (server contract from `getOrderIdsForPeriod`), so
 * `starts_at == period_end` means "launched the moment period ended" → outside.
 *
 * Timezone-correctness matters here. `starts_at` / `ends_at` are Postgres
 * `date` columns (calendar literals in the user's tz), while `period_start`
 * / `period_end` are full ISO timestamps already converted from that tz to
 * UTC. Naively parsing `starts_at` as `T00:00:00Z` gets the boundary wrong
 * for negative-offset zones — e.g. America/Los_Angeles, May period ends
 * `2026-06-01T07:00:00Z`; `starts_at = "2026-06-01"` parsed as `T00:00:00Z`
 * yields `2026-06-01T00:00:00Z`, which compares as BEFORE the period end
 * and falsely intersects. Parsing through `zonedTimeToUtc` aligns both
 * sides on the same moment-in-time.
 *
 * Defensive: bad parse on either side → return true (don't dim).
 */
export function lifetimeIntersectsPeriod(
  type: Pick<BundleTypePerformance, "starts_at" | "ends_at">,
  periodStart: string,
  periodEnd: string,
  timezone: string,
): boolean {
  const periodStartMs = Date.parse(periodStart)
  const periodEndMs = Date.parse(periodEnd)
  if (!Number.isFinite(periodStartMs) || !Number.isFinite(periodEndMs)) return true

  if (type.starts_at) {
    const startsMs = calendarLiteralToUtcMs(type.starts_at, timezone)
    if (Number.isFinite(startsMs) && startsMs >= periodEndMs) return false
  }
  if (type.ends_at) {
    const endsMs = calendarLiteralToUtcMs(type.ends_at, timezone)
    if (Number.isFinite(endsMs) && endsMs < periodStartMs) return false
  }
  return true
}


/**
 * Shared cost computation used by both Config and Bundle performance views.
 *
 * COGS: a pending inline edit replaces the row's COGS; otherwise the server-resolved
 * `total_cogs` (manual variant → manual product → Shopify cost, per line) plus the global
 * default for the units no line resolved. `cogs_complete` is false while some units have no
 * cost at all.
 * Refund cost: actual refund_amount when > 0, else revenue × return_rate
 * Shipping: exact per-order calculation via free_shipping_order_count
 */
export function computeEconomicsRow(
  raw: {
    total_revenue: number
    total_cogs: number | null
    /** Units covered by `total_cogs`. */
    units_with_cogs: number
    order_count: number
    units_sold: number
    units_returned: number
    allocated_shipping: number
    allocated_shopify_fee: number
    refund_amount: number
    free_shipping_order_count: number
  },
  params: EconomicsSettings,
  pendingCogsPerUnit?: number | null,
): ComputedEconomics {
  // Clamped: matrix virtual units are fractional and both counts are rounded separately.
  const uncoveredUnits = pendingCogsPerUnit != null ? 0 : Math.max(0, raw.units_sold - raw.units_with_cogs)
  const effective_cogs =
    pendingCogsPerUnit != null
      ? pendingCogsPerUnit * raw.units_sold
      : resolveRowCogs(raw.total_cogs, uncoveredUnits, params.default_cogs_per_unit)
  const cogs_available = effective_cogs != null
  const cogs_complete = cogs_available && (uncoveredUnits === 0 || params.default_cogs_per_unit > 0)

  const return_rate = raw.units_sold > 0 ? raw.units_returned / raw.units_sold : 0
  // Use actual refund amount when available; fall back to estimate from return rate
  const refund_cost = raw.refund_amount > 0 ? raw.refund_amount : raw.total_revenue * return_rate
  const return_labels_cost = raw.units_returned * params.return_label_cost
  // Shipping: exact per-order calculation based on order subtotal vs threshold
  const free_orders = raw.free_shipping_order_count
  const shipping_paid_by: ShippingPaidBy =
    free_orders === 0 ? "Customer"
      : free_orders >= raw.order_count ? "Brand"
        : "Mixed"
  const brand_shipping = free_orders * params.shipping_cost
  const total_cac = raw.order_count * params.blended_cac
  const shopify_fee =
    raw.allocated_shopify_fee > 0
      ? raw.allocated_shopify_fee
      : raw.total_revenue * params.shopify_fee_pct
  const cogs = effective_cogs ?? 0
  const net_profit =
    raw.total_revenue - cogs - total_cac - brand_shipping - shopify_fee - refund_cost - return_labels_cost
  const profit_per_order = raw.order_count > 0 ? net_profit / raw.order_count : 0
  const margin_pct = raw.total_revenue > 0 ? (net_profit / raw.total_revenue) * 100 : 0

  return {
    distinct_orders: raw.order_count,
    effective_cogs,
    cogs_available,
    cogs_complete,
    return_rate,
    refund_cost,
    return_labels_cost,
    shipping_paid_by,
    brand_shipping,
    total_cac,
    shopify_fee,
    net_profit,
    profit_per_order,
    margin_pct,
  }
}

/** Server-resolved COGS plus the global default for the units no line resolved. */
function resolveRowCogs(
  totalCogs: number | null,
  uncoveredUnits: number,
  defaultCogsPerUnit: number,
): number | null {
  const fallback = defaultCogsPerUnit > 0 ? defaultCogsPerUnit * uncoveredUnits : null
  if (totalCogs == null) return fallback
  return totalCogs + (fallback ?? 0)
}

// ---------------------------------------------------------------------------
// Bundle Performance (new endpoint shape)
// ---------------------------------------------------------------------------

export interface ComputedBundleTypePerformance {
  /** Group 1: bundle-side line_items only (UI period). */
  bundleComputed: ComputedEconomics
  /** Group 1: standalone-side line_items (UI period — this type's families, sold without bundle). */
  standaloneComputed: ComputedEconomics
  /**
   * Group 1 union: bundle ∪ standalone for THIS type over the UI period,
   * computed from type.current_all (NOT bundleComputed + standaloneComputed
   * sum — would double-count order-level metrics when one Shopify order has
   * both bundle and extra standalone components of the same family).
   */
  currentAllComputed: ComputedEconomics
  /**
   * Group 2 lifetime current — bundle ∪ standalone for THIS type over the
   * lifetime current window (`type.lifetime_current`). Symmetric counterpart
   * of `baselineComputed`; the two are 1:1 comparable because they have
   * identical length. null when baseline_status !== 'ok' or lifetime_current
   * is null.
   */
  lifetimeCurrentComputed: ComputedEconomics | null
  /** null when type.baseline_status !== 'ok' or type.baseline === null. */
  baselineComputed: ComputedEconomics | null
  /**
   * Lifetime profit lift — `lifetimeCurrentComputed.net_profit −
   * baselineComputed.net_profit`. Null when either side is missing. This is
   * the lifetime-scoped delta and is invariant under UI period changes.
   */
  profitLift: number | null
}

/**
 * Compute the economics blocks + lifetime Profit Lift for one bundle type
 * from the /bundle-performance API response. Pure — uses computeEconomicsRow
 * verbatim.
 *
 * Phase 2/3 contract (workbench `bundle-cannibalization-lifetime-scope`):
 * Profit Lift is computed as `lifetime_current − baseline`. Both windows are
 * identical-length lifetime slices, so the delta is methodologically clean
 * (vs the legacy `current_all − baseline`, which mixed UI-period current
 * with lifetime baseline after Phase 2 server changes).
 */
export function computeBundleTypePerformance(
  type: BundleTypePerformance,
  settings: EconomicsSettings,
): ComputedBundleTypePerformance {
  const bundleComputed = computeEconomicsRow(type.bundle, settings)
  const standaloneComputed = computeEconomicsRow(type.standalone, settings)
  const currentAllComputed = computeEconomicsRow(type.current_all, settings)

  const baselineComputed =
    type.baseline_status === "ok" && type.baseline != null
      ? computeEconomicsRow(type.baseline, settings)
      : null

  const lifetimeCurrentComputed =
    type.baseline_status === "ok" && type.lifetime_current != null
      ? computeEconomicsRow(type.lifetime_current, settings)
      : null

  const profitLift =
    baselineComputed == null || lifetimeCurrentComputed == null
      ? null
      : lifetimeCurrentComputed.net_profit - baselineComputed.net_profit

  return {
    bundleComputed,
    standaloneComputed,
    currentAllComputed,
    lifetimeCurrentComputed,
    baselineComputed,
    profitLift,
  }
}

/** Compute economics for a single product row from API data + params. */
export function computeProductEconomics(
  product: ConfigEconomicsItem,
  params: EconomicsSettings,
  pendingCogsPerUnit?: number | null,
): ComputedEconomics {
  return computeEconomicsRow(product, params, pendingCogsPerUnit ?? null)
}

/** Per-unit COGS a row is costed at — derived from the same total its profit uses. */
export function effectiveUnitCogs(
  computed: Pick<ComputedEconomics, "effective_cogs">,
  unitsSold: number,
): number | null {
  return computed.effective_cogs != null && unitsSold > 0 ? computed.effective_cogs / unitsSold : null
}

/**
 * Row identity key — MUST stay in lockstep with the server-side aggregation key
 * in `route.ts → aggregateByProduct` (group by `sku` alone; fall back to
 * `(product_id, variant_title)` only for empty/null SKU). The same physical SKU
 * under multiple Shopify product_ids collapses into one row. `title` is excluded
 * so a rename in `shopify_order_line_items.title` does not split a SKU. `|`
 * separator avoids collisions with `-`/`:` in SKUs.
 */
export function productRowKey(
  p: Pick<ConfigEconomicsItem, "product_id" | "sku" | "variant_title">,
): string {
  return p.sku != null && p.sku.length > 0
    ? `sku|${p.sku}`
    : `nosku|${p.product_id}|${p.variant_title ?? ""}`
}

/**
 * A row's pending inline COGS edit, keyed by `productRowKey`. The server resolves every
 * stored override per line, so this only bridges an edit until the revalidated rows
 * arrive — stored overrides never re-enter the row math here.
 */
export function resolveCogsOverride(
  row: Pick<ConfigEconomicsItem, "product_id" | "sku" | "variant_title">,
  pendingCogs: Readonly<Record<string, number>>,
): number | null {
  return pendingCogs[productRowKey(row)] ?? null
}

/** Set economics; `pendingCogs` holds inline edits awaiting the refetch. */
export function computeSetEconomics(
  products: ConfigEconomicsItem[],
  settings: EconomicsSettings,
  pendingCogs: Readonly<Record<string, number>> = {},
): ComputedEconomics {
  const distinctOrders = new Set(products.flatMap((p) => p.order_ids)).size
  const perProduct = products.map((p) =>
    computeProductEconomics(p, settings, resolveCogsOverride(p, pendingCogs)),
  )
  const cogs_available = perProduct.some((r) => r.cogs_available)
  const effective_cogs = cogs_available ? perProduct.reduce((s, r) => s + (r.effective_cogs ?? 0), 0) : null
  const cogs_complete = perProduct.every((r) => r.cogs_complete)
  return buildSetResult(products, perProduct, settings, effective_cogs, cogs_available, cogs_complete, distinctOrders)
}

function buildSetResult(
  products: ConfigEconomicsItem[],
  perProduct: ComputedEconomics[],
  settings: EconomicsSettings,
  effective_cogs: number | null,
  cogs_available: boolean,
  cogs_complete: boolean,
  distinctOrders: number,
): ComputedEconomics {
  const brand_shipping = perProduct.reduce((s, r) => s + r.brand_shipping, 0)
  const shopify_fee = perProduct.reduce((s, r) => s + r.shopify_fee, 0)
  const total_cac = distinctOrders * settings.blended_cac
  const refund_cost = perProduct.reduce((s, r) => s + r.refund_cost, 0)
  const return_labels_cost = perProduct.reduce((s, r) => s + r.return_labels_cost, 0)
  const totalUnitsSold = products.reduce((s, p) => s + p.units_sold, 0)
  const totalUnitsReturned = products.reduce((s, p) => s + p.units_returned, 0)
  const totalRevenue = products.reduce((s, p) => s + p.total_revenue, 0)
  const return_rate = totalUnitsSold > 0 ? totalUnitsReturned / totalUnitsSold : 0
  const cogs = effective_cogs ?? 0
  const net_profit = totalRevenue - cogs - total_cac - brand_shipping - shopify_fee - refund_cost - return_labels_cost
  const profit_per_order = distinctOrders > 0 ? net_profit / distinctOrders : 0
  const margin_pct = totalRevenue > 0 ? (net_profit / totalRevenue) * 100 : 0

  return {
    distinct_orders: distinctOrders,
    effective_cogs, cogs_available, cogs_complete, return_rate, refund_cost, return_labels_cost,
    shipping_paid_by: brand_shipping === 0 ? "Customer"
      : perProduct.every((r) => r.shipping_paid_by === "Brand") ? "Brand"
        : "Mixed",
    brand_shipping, total_cac, shopify_fee, net_profit, profit_per_order, margin_pct,
  }
}

/**
 * Sum an array of ComputedEconomics rows into a single totals row.
 * Used for bottom-up Totals: guarantees Totals = sum of visible rows.
 */
export function sumComputedRows(
  rows: ComputedEconomics[],
  totalOrders: number,
  totalRevenue: number,
  totalUnitsSold: number,
  totalUnitsReturned: number,
  settings: EconomicsSettings,
): ComputedEconomics {
  if (rows.length === 0) {
    return {
      distinct_orders: 0,
      effective_cogs: null, cogs_available: false, cogs_complete: false, return_rate: 0, refund_cost: 0,
      return_labels_cost: 0, shipping_paid_by: "Customer", brand_shipping: 0,
      total_cac: 0, shopify_fee: 0, net_profit: 0, profit_per_order: 0,
      margin_pct: 0,
    }
  }

  const cogs_available = rows.some((r) => r.cogs_available)
  const effective_cogs = cogs_available ? rows.reduce((s, r) => s + (r.effective_cogs ?? 0), 0) : null
  const cogs_complete = rows.every((r) => r.cogs_complete)
  const brand_shipping = rows.reduce((s, r) => s + r.brand_shipping, 0)
  const shopify_fee = rows.reduce((s, r) => s + r.shopify_fee, 0)
  // Use distinct order count for CAC — avoids double-counting shared orders
  const total_cac = totalOrders * settings.blended_cac
  const refund_cost = rows.reduce((s, r) => s + r.refund_cost, 0)
  const return_labels_cost = rows.reduce((s, r) => s + r.return_labels_cost, 0)
  const return_rate = totalUnitsSold > 0 ? totalUnitsReturned / totalUnitsSold : 0
  const cogs = effective_cogs ?? 0
  const net_profit = totalRevenue - cogs - total_cac - brand_shipping - shopify_fee - refund_cost - return_labels_cost
  const profit_per_order = totalOrders > 0 ? net_profit / totalOrders : 0
  const margin_pct = totalRevenue > 0 ? (net_profit / totalRevenue) * 100 : 0

  return {
    distinct_orders: totalOrders,
    effective_cogs, cogs_available, cogs_complete,
    return_rate, refund_cost, return_labels_cost,
    shipping_paid_by: brand_shipping === 0 ? "Customer"
      : rows.every((r) => r.shipping_paid_by === "Brand") ? "Brand"
        : "Mixed",
    brand_shipping, total_cac, shopify_fee, net_profit, profit_per_order, margin_pct,
  }
}
